import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { gridLayoutService, POS_GRID_PRODUCTS_MAX_IDS, productService } from '@/services'
import { collectLayoutReferencedProductIds } from '@/utils/gridLayout'
import { normalizePosProductRecord } from '@/utils/posProduct'

/**
 * Grid product index for the POS register.
 * Holds all register-visible products (drill-eligible + layout-placed refs), not the full catalog.
 */
export const useProductsStore = defineStore('products', () => {
  /** @type {import('vue').Ref<Map<number, object>>} */
  const byId = ref(new Map())
  const loading = ref(false)
  const error = ref(null)
  const lastFetchedAt = ref(null)

  /** @type {Set<number>} */
  const pendingEnsureIds = new Set()
  /** @type {Promise<void>|null} */
  let ensureFlushTimer = null
  /** @type {Promise<void>|null} */
  let ensureFlushPromise = null

  const items = computed(() => Array.from(byId.value.values()))

  const getAll = computed(() => items.value)

  const getById = (id) => {
    if (id === undefined || id === null) return null
    const numericId = Number(id)
    if (!Number.isFinite(numericId)) return null
    return byId.value.get(numericId) || null
  }

  const getByCategoryId = (categoryId) => {
    if (!categoryId) return []
    const catId = Number(categoryId)
    return items.value.filter(p => Number(p.category_id) === catId)
  }

  const replaceByIdMap = (nextMap) => {
    byId.value = nextMap
  }

  const upsertPosProduct = (raw) => {
    const normalized = normalizePosProductRecord(raw)
    if (!normalized) return null

    const next = new Map(byId.value)
    const existing = next.get(normalized.id)
    next.set(normalized.id, existing ? { ...existing, ...normalized } : normalized)
    replaceByIdMap(next)
    return next.get(normalized.id)
  }

  const hydrateGridProducts = (records) => {
    const next = new Map()
    for (const raw of records || []) {
      const normalized = normalizePosProductRecord(raw)
      if (normalized) next.set(normalized.id, normalized)
    }
    replaceByIdMap(next)
    lastFetchedAt.value = Date.now()
  }

  const chunkIds = (ids, size = POS_GRID_PRODUCTS_MAX_IDS) => {
    const chunks = []
    for (let i = 0; i < ids.length; i += size) {
      chunks.push(ids.slice(i, i + size))
    }
    return chunks
  }

  const fetchGridProductsByIds = async (ids) => {
    const unique = [...new Set((ids || []).map(Number).filter((n) => n > 0))]
    if (!unique.length) return []

    const rows = []
    for (const chunk of chunkIds(unique)) {
      const batch = await gridLayoutService.fetchGridProducts(chunk, {
        includeHasPosModifiers: true
      })
      rows.push(...(batch || []))
    }
    return rows
  }

  const flushPendingEnsureIds = async () => {
    if (!pendingEnsureIds.size) return

    const ids = [...pendingEnsureIds]
    pendingEnsureIds.clear()
    ensureFlushTimer = null

    try {
      const rows = await fetchGridProductsByIds(ids)
      for (const raw of rows) {
        upsertPosProduct(raw)
      }
    } catch (e) {
      console.error('Failed to batch-fetch grid products:', e)
      for (const id of ids) {
        try {
          const raw = await productService.getById(id)
          if (raw) upsertPosProduct(raw)
        } catch (inner) {
          console.error(`Failed to fetch POS product ${id}:`, inner)
        }
      }
    }
  }

  const scheduleEnsureFlush = () => {
    if (ensureFlushTimer) return ensureFlushPromise

    ensureFlushPromise = new Promise((resolve) => {
      ensureFlushTimer = setTimeout(async () => {
        try {
          await flushPendingEnsureIds()
        } finally {
          ensureFlushPromise = null
          resolve()
        }
      }, 16)
    })

    return ensureFlushPromise
  }

  /**
   * Resolve a product into the grid index, batch-fetching missing ids when possible.
   */
  const ensureById = async (id) => {
    const existing = getById(id)
    if (existing) return existing

    const numericId = Number(id)
    if (!Number.isFinite(numericId) || numericId <= 0) return null

    pendingEnsureIds.add(numericId)
    await scheduleEnsureFlush()
    return getById(numericId)
  }

  /**
   * Preload all register-visible products: drill-visible union plus layout-only refs.
   */
  const preloadGridProducts = async (options = {}) => {
    const { force = false, layouts: layoutsOption } = options

    if (loading.value) return

    let layouts = layoutsOption
    if (!layouts) {
      const { useGridStore } = await import('@/stores/grid')
      layouts = useGridStore().layouts ?? {}
    }

    const layoutIds = collectLayoutReferencedProductIds(layouts)

    if (!force && lastFetchedAt.value && (Date.now() - lastFetchedAt.value < 5 * 60 * 1000)) {
      const hasLayoutRefs = layoutIds.every((id) => getById(id))
      if (hasLayoutRefs && byId.value.size > 0) {
        return
      }
    }

    loading.value = true
    error.value = null

    try {
      const visibleRows = await gridLayoutService.fetchGridVisibleProducts({
        includeHasPosModifiers: true
      })
      for (const raw of visibleRows) {
        upsertPosProduct(raw)
      }

      const missingLayoutIds = layoutIds.filter((id) => !getById(id))
      if (missingLayoutIds.length) {
        const layoutRows = await fetchGridProductsByIds(missingLayoutIds)
        for (const raw of layoutRows) {
          upsertPosProduct(raw)
        }
      }

      lastFetchedAt.value = Date.now()
    } catch (e) {
      console.error('Failed to preload grid products:', e)
      error.value = e?.message || 'Failed to preload grid products'
    } finally {
      loading.value = false
    }
  }

  /** @deprecated Use preloadGridProducts — kept temporarily for call-site migration. */
  const preloadForPos = preloadGridProducts

  const reset = () => {
    replaceByIdMap(new Map())
    loading.value = false
    error.value = null
    lastFetchedAt.value = null
    pendingEnsureIds.clear()
    if (ensureFlushTimer) {
      clearTimeout(ensureFlushTimer)
      ensureFlushTimer = null
    }
    ensureFlushPromise = null
  }

  return {
    byId,
    items,
    loading,
    error,
    lastFetchedAt,
    getAll,
    getById,
    getByCategoryId,
    upsertPosProduct,
    hydrateGridProducts,
    fetchGridProductsByIds,
    ensureById,
    preloadGridProducts,
    preloadForPos,
    reset
  }
})
